// 종목별 상세 보기 — 풀스크린 페이지
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchHoldingsDetail } from '../api/mobileApi';
import '../styles/mobile.css';

const fNum   = v => (v == null ? '-' : Math.round(Number(v)).toLocaleString('ko-KR'));
const fSign  = v => (Number(v) > 0 ? '+' : '');
const colorCls = v => (Number(v) > 0 ? 'pos' : Number(v) < 0 ? 'neg' : 'neu');

const fQty = v =>
  v == null ? '-' : Number(v).toLocaleString('ko-KR', { maximumFractionDigits: 4 });

export default function DashboardStockDetailPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const accounts = state?.accounts ?? [];

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  const acctParam = accounts.join(',');

  useEffect(() => {
    setLoading(true);
    fetchHoldingsDetail(acctParam)
      .then(d => setData(Array.isArray(d) ? d : []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [acctParam]);

  // 계좌별 그룹화
  const { acctOrder, acctMap } = React.useMemo(() => {
    const order = [];
    const map   = {};
    (data || []).forEach(r => {
      if (!map[r.account_name]) {
        map[r.account_name] = [];
        order.push(r.account_name);
      }
      map[r.account_name].push(r);
    });
    return { acctOrder: order, acctMap: map };
  }, [data]);

  // 계좌별 소계
  const acctStats = acctOrder.map(acct => {
    const items  = acctMap[acct];
    const subBuy  = items.reduce((s, r) => s + (r.buy_amount  || 0), 0);
    const subEval = items.reduce((s, r) => s + (r.eval_amount || 0), 0);
    return { acct, items, subBuy, subEval, subProfit: subEval - subBuy };
  });

  const grandBuy    = acctStats.reduce((s, a) => s + a.subBuy,    0);
  const grandEval   = acctStats.reduce((s, a) => s + a.subEval,   0);
  const grandProfit = grandEval - grandBuy;

  return (
    <div className="mb-root mb-back-layout">
      {/* 헤더 */}
      <div className="mb-back-header">
        <div className="mb-back-header-row">
          <button className="mb-back-btn" onClick={() => navigate(-1)}>‹</button>
          <span className="mb-back-header-title">종목별 실시간 현황</span>
        </div>
      </div>

      {/* 본문 */}
      <div className="mb-back-content">
        {loading ? (
          <div className="mb-loading">불러오는 중...</div>
        ) : !data?.length ? (
          <div className="mb-empty">보유 종목이 없습니다.</div>
        ) : (
          <>
            {acctStats.map(({ acct, items, subBuy, subEval, subProfit }) => (
              <React.Fragment key={acct}>
                {/* 계좌 헤더 */}
                <div className="mb-acct-header">
                  <span className="mb-acct-header-name">{acct}</span>
                  <span className="mb-acct-header-sub">
                    매수 {fNum(subBuy)}원
                  </span>
                </div>

                {/* 종목 리스트 */}
                {items.map((r, i) => {
                  const ep  = r.eval_profit      ?? 0;
                  const er  = r.eval_profit_rate ?? 0;
                  const qty = fQty(r.quantity);
                  const cur = r.current_price != null ? `${fNum(r.current_price)}원` : '시세없음';
                  return (
                    <div key={i} className="mb-stock-item">
                      <div className="mb-stock-item-left">
                        <div className="mb-stock-name">{r.stock_name}</div>
                        <div className="mb-stock-meta">
                          {qty}주 · 현재가 {cur}
                        </div>
                        {r.today_diff != null && (
                          <div className={`mb-stock-meta ${colorCls(r.today_diff)}`}>
                            오늘 {fSign(r.today_diff)}{fNum(r.today_diff)}원
                            {r.today_diff_rate != null
                              ? ` (${fSign(r.today_diff_rate)}${Number(r.today_diff_rate).toFixed(2)}%)`
                              : ''}
                          </div>
                        )}
                      </div>
                      <div className="mb-stock-item-right">
                        <div className={`mb-stock-profit ${colorCls(ep)}`}>
                          {fSign(ep)}{fNum(ep)}원
                        </div>
                        <div className={`mb-stock-rate ${colorCls(er)}`}>
                          {fSign(er)}{Number(er).toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* 계좌 소계 */}
                <div className="mb-subtotal-row">
                  <span className="mb-subtotal-label">{acct} 소계 | 평가 {fNum(subEval)}원</span>
                  <span className={`mb-subtotal-profit ${colorCls(subProfit)}`}>
                    {fSign(subProfit)}{fNum(subProfit)}원
                  </span>
                </div>
              </React.Fragment>
            ))}

            {/* 전체 합계 */}
            <div
              className="mb-subtotal-row"
              style={{ background: '#eef2ff', fontWeight: 700 }}
            >
              <span style={{ fontSize: 13, color: '#333' }}>
                전체 합계 | 평가 {fNum(grandEval)}원
              </span>
              <span className={`mb-subtotal-profit ${colorCls(grandProfit)}`}
                    style={{ fontSize: 16 }}>
                {fSign(grandProfit)}{fNum(grandProfit)}원
              </span>
            </div>

            {/* 하단 여백 */}
            <div style={{ height: 24 }} />
          </>
        )}
      </div>
    </div>
  );
}
